import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogScrollArea,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Smartphone,
  QrCode,
  Phone,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
  RefreshCw,
  MessageSquare,
  Crown,
} from 'lucide-react';
import QRCode from 'react-qr-code';
import {
  whatsappService,
  WhatsAppConnectionCode,
  WhatsAppConnectionStatus,
  connectionCodeFromString,
  DEFAULT_WHATSAPP_BUSINESS_PHONE,
  isValidConnectionCode,
  INVALID_CONNECTION_CODE_ERROR,
} from '../../../services/whatsappService';
import { toast } from 'sonner';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import {
  WHATSAPP_PHONE_LIMITS,
  type WhatsAppPlanTier,
} from './whatsappPhoneLimits';

export type { WhatsAppPlanTier };

interface WhatsAppIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integrationData: any) => Promise<any>;
  socket?: SpaceSocket;
  spaceId: string;
  integrationId: string;
  integrationName: string;
  userTier: WhatsAppPlanTier;
  existingIntegration?: {
    bindingId: string;
    binding: any;
    socket: any;
  };
}

const TIER_COLORS: Record<WhatsAppPlanTier, string> = {
  free: 'bg-gray-100 text-gray-800',
  starter: 'bg-emerald-100 text-emerald-800',
  pro: 'bg-blue-100 text-blue-800',
  enterprise: 'bg-amber-100 text-amber-800',
};

export function toWhatsAppPlanTier(tier: string): WhatsAppPlanTier {
  const normalized = tier.toLowerCase();
  if (
    normalized === 'free' ||
    normalized === 'starter' ||
    normalized === 'pro' ||
    normalized === 'enterprise'
  ) {
    return normalized;
  }
  return 'free';
}

export const WhatsAppIntegrationModal: React.FC<WhatsAppIntegrationModalProps> = ({
  isOpen,
  onClose,
  onCreateIntegration,
  socket,
  spaceId,
  integrationId,
  integrationName,
  userTier,
  existingIntegration,
}) => {
  const { t } = useTranslation('integrations');
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [, setSocketError] = useState<string | null>(null);
  const [connectionCode, setConnectionCode] = useState<WhatsAppConnectionCode | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<WhatsAppConnectionStatus | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);
  const [isCreatingIntegration, setIsCreatingIntegration] = useState(false);
  const bootstrapRef = useRef<{ bindingId: string | null; inFlight: boolean }>({
    bindingId: null,
    inFlight: false,
  });

  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(
            spaceId,
            integrationRegistry.whatsapp_basic
          );
          setSelectedSocket(defaultSocket);
        } catch (loadError) {
          console.error('Failed to get or create default socket:', loadError);
          setSocketError(
            loadError instanceof Error ? loadError.message : 'Failed to load socket'
          );
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);

  useEffect(() => {
    if (isOpen) {
      if (existingIntegration) {
        setCreatedIntegration({
          binding_id: existingIntegration.bindingId,
          ...existingIntegration.binding,
        });
      } else if (!createdIntegration && selectedSocket && !socketLoading) {
        createIntegrationBinding();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createIntegrationBinding is stable for open/socket transitions
  }, [isOpen, existingIntegration, selectedSocket, socketLoading]);

  const loadConnectionStatus = useCallback(
    async (bindingId?: string): Promise<WhatsAppConnectionStatus | null> => {
      const id = bindingId ?? createdIntegration?.binding_id;
      if (!id) return null;

      try {
        setIsLoadingStatus(true);
        const response = await whatsappService.getConnectionStatus(id);
        setConnectionStatus(response);
        return response;
      } catch (statusError) {
        console.error('Error loading connection status:', statusError);
        const fallback: WhatsAppConnectionStatus = {
          connected_numbers: [],
          total_connections: 0,
          connection_code: null,
          integration_status: 'inactive',
        };
        setConnectionStatus(fallback);
        return fallback;
      } finally {
        setIsLoadingStatus(false);
      }
    },
    [createdIntegration?.binding_id]
  );

  const handleGenerateCode = useCallback(
    async (bindingId?: string) => {
      const id = bindingId ?? createdIntegration?.binding_id;
      if (!id) return;

      try {
        setIsGeneratingCode(true);
        setError(null);
        const response = await whatsappService.generateConnectionCode(id);
        setConnectionCode(response);
        await loadConnectionStatus(id);
      } catch (generateError) {
        console.error('Error generating connection code:', generateError);
        const message =
          generateError instanceof Error &&
          generateError.message === INVALID_CONNECTION_CODE_ERROR
            ? t('whatsapp.errors.invalidCodeResponse')
            : t('whatsapp.errors.generateFailed');
        setError(message);
        setConnectionCode(null);
      } finally {
        setIsGeneratingCode(false);
      }
    },
    [createdIntegration?.binding_id, loadConnectionStatus, t]
  );

  const bootstrapConnectionSetup = useCallback(
    async (bindingId: string) => {
      try {
        setIsBootstrapping(true);
        setError(null);

        const status = await loadConnectionStatus(bindingId);
        const existingCode = status?.connection_code?.trim();
        if (existingCode) {
          const reused = connectionCodeFromString(existingCode);
          if (isValidConnectionCode(reused)) {
            setConnectionCode(reused);
            return;
          }
        }

        await handleGenerateCode(bindingId);
      } catch (bootstrapError) {
        console.error('Error bootstrapping WhatsApp connection setup:', bootstrapError);
        setError(t('whatsapp.errors.bootstrapFailed'));
      } finally {
        setIsBootstrapping(false);
      }
    },
    [handleGenerateCode, loadConnectionStatus, t]
  );

  useEffect(() => {
    const bindingId = createdIntegration?.binding_id;
    if (!bindingId) return;
    if (bootstrapRef.current.inFlight) return;
    if (bootstrapRef.current.bindingId === bindingId && connectionCode) return;

    bootstrapRef.current.inFlight = true;
    bootstrapConnectionSetup(bindingId).finally(() => {
      bootstrapRef.current.inFlight = false;
      bootstrapRef.current.bindingId = bindingId;
    });
  }, [createdIntegration?.binding_id, bootstrapConnectionSetup, connectionCode]);

  useEffect(() => {
    if (!isOpen) {
      setConnectionCode(null);
      setConnectionStatus(null);
      setError(null);
      setIsBootstrapping(false);
      bootstrapRef.current = { bindingId: null, inFlight: false };
      if (!existingIntegration) {
        setCreatedIntegration(null);
      }
    }
  }, [isOpen, existingIntegration]);

  const createIntegrationBinding = async () => {
    if (isCreatingIntegration || createdIntegration || !selectedSocket) return;

    try {
      setIsCreatingIntegration(true);
      setError(null);

      const integrationData = {
        integration_type: integrationId,
        integration_name: integrationName,
        permissions: buildBindingPermissions({
          flags: {
            can_read: true,
            can_write: true,
            can_search: true,
            can_delete: false,
          },
          categoriesComma: '',
          excludeTagsComma: '',
          minImportance: 1,
          includeRateLimit: false,
        }),
        auth_config: {
          auth_method: 'whatsapp_integration',
          twilio_whatsapp_number: DEFAULT_WHATSAPP_BUSINESS_PHONE,
        },
        socket_id: selectedSocket.socket_id,
      };

      const result = await onCreateIntegration(integrationData);
      if (!result?.binding_id) {
        setError(t('whatsapp.errors.missingBindingId'));
        return;
      }
      setCreatedIntegration(result);
    } catch (err) {
      console.error('Error creating WhatsApp integration binding:', err);
      setError(t('whatsapp.errors.createFailed'));
    } finally {
      setIsCreatingIntegration(false);
    }
  };

  const handleDisconnectPhone = async (phoneNumber: string) => {
    if (!createdIntegration?.binding_id) return;

    try {
      await whatsappService.disconnectPhoneNumber(createdIntegration.binding_id, phoneNumber);
      toast.success(`Phone number ${phoneNumber} disconnected successfully`);
      await loadConnectionStatus();
    } catch (err) {
      console.error('Error disconnecting phone:', err);
      toast.error('Failed to disconnect phone number');
    }
  };

  const handleRefreshStatus = () => {
    loadConnectionStatus();
  };

  const handleRegenerateQr = () => {
    handleGenerateCode();
  };

  const getStatusIcon = () => {
    if (!connectionStatus) return <Clock className="w-4 h-4 text-gray-500" />;

    switch (connectionStatus.integration_status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'inactive':
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    if (!connectionStatus) return 'Loading...';

    switch (connectionStatus.integration_status) {
      case 'active':
        return `Connected (${connectionStatus.total_connections} phone${connectionStatus.total_connections !== 1 ? 's' : ''})`;
      case 'pending':
        return 'Waiting for connection...';
      case 'inactive':
        return 'Not connected';
      default:
        return 'Unknown status';
    }
  };

  const phoneLimit = WHATSAPP_PHONE_LIMITS[userTier];

  const canAddMorePhones = () => {
    if (phoneLimit === null) return true;
    const current = connectionStatus?.total_connections || 0;
    return current < phoneLimit;
  };

  const getRemainingSlots = () => {
    if (phoneLimit === null) return null;
    const current = connectionStatus?.total_connections || 0;
    return Math.max(0, phoneLimit - current);
  };

  const remainingSlots = getRemainingSlots();

  const isSetupLoading = isBootstrapping || isGeneratingCode;
  const setupLoadingLabel = isSetupLoading
    ? t('whatsapp.generatingCode')
    : t('whatsapp.loadingSetup');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="scrollable" className="max-w-2xl">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-green-500" />
            WhatsApp Basic Integration
          </DialogTitle>
        </DialogHeader>

        <DialogScrollArea className="space-y-6 px-6">
          {isCreatingIntegration && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Setting up WhatsApp Integration
                </CardTitle>
                <CardDescription>
                  Creating your WhatsApp integration binding...
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {createdIntegration && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{integrationName}</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon()}
                    <span className="text-sm text-gray-600">{getStatusText()}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRefreshStatus}
                      disabled={isLoadingStatus}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoadingStatus ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription>
                  Send WhatsApp messages to create memories in your Memara space
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {spaceId && !existingIntegration && (
            <div className="mt-4">
              <AdvancedSocketSelection
                integration={integrationRegistry.whatsapp_basic}
                spaceId={spaceId}
                currentSocket={selectedSocket}
                onSocketChange={setSelectedSocket}
                disabled={isCreatingIntegration}
              />
            </div>
          )}

          {createdIntegration && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Plan Limits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className={TIER_COLORS[userTier]}>
                      {userTier.charAt(0).toUpperCase() + userTier.slice(1)} Plan
                    </Badge>
                    <p className="text-sm text-gray-600 mt-1">
                      {phoneLimit === null ? 'Unlimited' : phoneLimit} phone
                      number{phoneLimit !== 1 ? 's' : ''} allowed
                    </p>
                    {userTier === 'free' && (
                      <p className="text-sm text-gray-600 mt-1">
                        {t('whatsapp.voiceNotesPaidPlan')}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      {remainingSlots === null
                        ? 'Unlimited slots remaining'
                        : `${remainingSlots} slot${remainingSlots !== 1 ? 's' : ''} remaining`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {error && !createdIntegration && !isCreatingIntegration && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {createdIntegration &&
            (!connectionStatus ||
              connectionStatus.integration_status === 'inactive' ||
              canAddMorePhones()) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-blue-500" />
                    Connect Your Phone
                  </CardTitle>
                  <CardDescription>
                    {!connectionCode
                      ? t('whatsapp.generatingQrDescription')
                      : t('whatsapp.scanQrDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {error && createdIntegration && (
                    <Alert className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {error}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleGenerateCode()}
                          className="ml-2"
                          disabled={isGeneratingCode}
                        >
                          {t('whatsapp.tryAgain')}
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                  {!connectionCode && !error ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-sm text-gray-600">{setupLoadingLabel}</span>
                    </div>
                  ) : connectionCode ? (
                    <div className="space-y-4">
                      <div className="flex justify-center">
                        <QRCode
                          value={connectionCode.qr_code_data}
                          size={200}
                          className="border rounded-lg p-4"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium">Connection Code:</span>
                          <code className="text-sm bg-white px-2 py-1 rounded border">
                            {connectionCode.code}
                          </code>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium">WhatsApp Number:</span>
                          <span className="text-sm font-mono">
                            {connectionCode.business_phone}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRegenerateQr}
                          disabled={isGeneratingCode}
                        >
                          <RefreshCw
                            className={`w-4 h-4 mr-2 ${isGeneratingCode ? 'animate-spin' : ''}`}
                          />
                          {t('whatsapp.regenerateQr')}
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Instructions:</h4>
                        <ol className="text-sm text-gray-600 space-y-1 pl-4">
                          <li>1. Scan the QR code with your phone&apos;s camera</li>
                          <li>
                            2. Or send your connection code to{' '}
                            <strong>{connectionCode.business_phone}</strong>
                          </li>
                          <li>3. Wait for confirmation that your phone is connected</li>
                        </ol>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

          {connectionStatus && connectionStatus.connected_numbers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-500" />
                  Connected Phone Numbers
                </CardTitle>
                <CardDescription>
                  Phone numbers connected to this integration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {connectionStatus.connected_numbers.map((phone, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-green-500" />
                        <div>
                          <p className="font-medium">
                            {whatsappService.formatPhoneNumber(phone.phone_number)}
                          </p>
                          <p className="text-sm text-gray-600">
                            Connected {new Date(phone.connected_at).toLocaleDateString()}
                            {phone.last_message_at && (
                              <span className="ml-2">
                                • Last message{' '}
                                {new Date(phone.last_message_at).toLocaleDateString()}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisconnectPhone(phone.phone_number)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                How to Use
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    Send any message to{' '}
                    {connectionCode?.business_phone || DEFAULT_WHATSAPP_BUSINESS_PHONE} to create a
                    memory
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>Messages will be automatically saved to your Memara space</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>You&apos;ll receive a confirmation message when your memory is created</p>
                </div>
              </div>
            </CardContent>
          </Card>

        </DialogScrollArea>
        <DialogFooter variant="scrollable" className="px-6 pb-6">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
